import React, { useState } from 'react';
import type { PriorityBucket } from 'shared/src/wrkthru/status-codes';
import { BUCKETS } from './bucket-theme';

type IssueSectionProps = {
  bucket: PriorityBucket;
  count: number;
  /** Open by default so the page reads as one priority-ordered stack. */
  defaultOpen?: boolean;
  children?: React.ReactNode;
};

export function IssueSection({ bucket, count, defaultOpen, children }: IssueSectionProps) {
  const meta = BUCKETS[bucket];
  const [open, setOpen] = useState(defaultOpen ?? true);
  const isEmpty = count === 0;

  return (
    // Borders live on the header/body, not the section — a section border leaves stubs when the header pins.
    <section className="overflow-clip rounded-lg bg-white">
      {/* bg-background hides body borders scrolling past the button's rounded corners */}
      <div className="sticky top-0 z-10 bg-background">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className={[
            'flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-[filter]',
            'border-x border-t border-slate-200',
            open ? 'rounded-t-lg' : 'rounded-lg border-b',
            'hover:brightness-95',
            meta.accent.bg
          ].join(' ')}
          aria-expanded={open}
          title={meta.description}>
          <span
            className={['text-base font-bold uppercase tracking-wide', meta.accent.text].join(' ')}>
            {meta.label}
          </span>
          <div className={['flex items-center gap-3', meta.accent.text].join(' ')}>
            <span className="text-sm font-semibold uppercase tracking-wide">{count}</span>
            <Chevron open={open} />
          </div>
        </button>
      </div>
      {open && !isEmpty && (
        <div className="divide-y divide-slate-100 rounded-b-lg border border-l-slate-200 border-r-slate-200 border-b-slate-200 border-t-slate-100 bg-white">
          {children}
        </div>
      )}
      {open && isEmpty && (
        <div className="rounded-b-lg border border-l-slate-200 border-r-slate-200 border-b-slate-200 border-t-slate-100 bg-white px-4 py-3 text-xs italic text-slate-500">
          Nothing here right now.
        </div>
      )}
    </section>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      className={`transition-transform ${open ? 'rotate-90' : ''}`}
      aria-hidden="true">
      <path
        d="M5 3l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
